import { ForceTheme } from 'src/common/components/force-theme';
import { ThemeColorMode } from 'src/common/enums';
import { useGetUserQuery } from 'src/common/slices';
import { useActiveCampaign } from 'src/features/campaigns/hooks/use-active-campaign';
import { SubscribeNowLabel, useTrackSubscribeNow } from 'src/features/campaigns/hooks/use-track-subscribe-now';
import { useYearlyPricing } from 'src/features/campaigns/hooks/use-yearly-pricing';
import { Countdown } from '../../components/countdown';
import { useSubscribeNowCtaLink } from '../../hooks/use-subscribe-now-cta-link';
import { useCampaignCta } from '../hooks';
import { SubscribeButton } from '../subscribe-button';
import { StyledBanner, StyledButtons, StyledCountdownContainer, StyledHeading, StyledSubTitle, StyledSubTitleAlt, StyledTitle, StyledTreasureChestImg } from './banner.styles';
import type { TicketingEventCampaignProps } from './banner.types';
import { DiscountMessage } from './components/discount-message';
export const TicketingEventCampaignBanner = ({
  title,
  subtitle,
  subtitleAlt,
  isLarge,
  ctaLabel,
  ctaName,
  onCtaClick
}: TicketingEventCampaignProps) => {
  const userQuery = useGetUserQuery();
  const {
    campaign
  } = useActiveCampaign();
  const {
    yearlyPrice,
    discountedYearlyPrice,
    symbol
  } = useYearlyPricing();
  const ctaLink = useSubscribeNowCtaLink(campaign);
  const trackSubscribeNow = useTrackSubscribeNow();
  const user = userQuery.data?.data?.user;
  const isPremium = Boolean(user?.isPremium);
  if (!campaign) return null;
  const onClick = () => {
    trackSubscribeNow(ctaName);
    onCtaClick?.();
  };
  return <ForceTheme themeMode={ThemeColorMode.DARK} data-sentry-element="ForceTheme" data-sentry-component="TicketingEventCampaignBanner" data-sentry-source-file="banner.tsx">
      <StyledBanner data-sentry-element="StyledBanner" data-sentry-source-file="banner.tsx">
        <StyledTreasureChestImg $large={isLarge} alt="" aria-hidden="true" data-sentry-element="StyledTreasureChestImg" data-sentry-source-file="banner.tsx" />
        <StyledHeading $large={isLarge} data-sentry-element="StyledHeading" data-sentry-source-file="banner.tsx">
          <StyledTitle data-testid="campaign-title" data-sentry-element="StyledTitle" data-sentry-source-file="banner.tsx">{title}</StyledTitle>
          {subtitle && <StyledSubTitle data-testid="campaign-promotion-text">{subtitle}</StyledSubTitle>}
          {subtitleAlt && <StyledSubTitleAlt data-testid="campaign-promotion-text-alt">{subtitleAlt}</StyledSubTitleAlt>}
        </StyledHeading>
        <StyledCountdownContainer data-sentry-element="StyledCountdownContainer" data-sentry-source-file="banner.tsx">
          <Countdown vertical dark endTime={campaign.endTime} data-sentry-element="Countdown" data-sentry-source-file="banner.tsx" />
        </StyledCountdownContainer>
        <DiscountMessage isPremium={isPremium} yearlyPrice={yearlyPrice} discountedYearlyPrice={discountedYearlyPrice} symbol={symbol} data-sentry-element="DiscountMessage" data-sentry-source-file="banner.tsx" />
        <StyledButtons data-sentry-element="StyledButtons" data-sentry-source-file="banner.tsx">
          <SubscribeButton to={ctaLink} aria-label={ctaLabel} onClick={onClick} data-sentry-element="SubscribeButton" data-sentry-source-file="banner.tsx">
            {ctaLabel}
          </SubscribeButton>
        </StyledButtons>
      </StyledBanner>
    </ForceTheme>;
};
export const withModalStyling = (BaseComponent: typeof TicketingEventCampaignBanner) => {
  return function ModalCampaignBanner(props: {
    onClick: () => void;
  }) {
    const {
      campaign
    } = useActiveCampaign();
    const ctaText = useCampaignCta(campaign);
    return <BaseComponent title={<>
            Save <span>{campaign?.amount}%</span> on annual subscriptions & triple the raffle tickets you earn!
          </>} subtitle={<>
            Power up your chances to win the Hack2Win Raffle prizes! Only annual subscribers can earn 3 tickets per
            room.
          </>} subtitleAlt={undefined} isLarge={false} ctaLabel={ctaText} ctaName={SubscribeNowLabel.FlashSaleModal} onCtaClick={props.onClick} />;
  };
};
export const withLandingStyling = (BaseComponent: typeof TicketingEventCampaignBanner) => {
  return function LandingHack2WinFlashSaleBanner() {
    const {
      campaign
    } = useActiveCampaign();
    const amount = campaign?.amount ?? 25;
    return <BaseComponent title={<>POWER UP YOUR CHANCES TO WIN THE HACK2WIN RAFFLE PRIZES!</>} subtitle={undefined} subtitleAlt={<>
            Save <span>{amount}%</span> on annual subscriptions & get <span>3x</span> tickets per room
          </>} isLarge={true} ctaLabel={`Save ${amount}% & earn more tickets!`} ctaName={SubscribeNowLabel.Hack2WinFlashSaleBanner} />;
  };
};